/**
 * Presence Store
 * Tracks online user IDs in real-time via Socket.IO events.
 * Single source of truth for "is this user online right now."
 */

import { create } from 'zustand';
import type { UserStatus } from '@/utils/presence.utils';

interface PresenceState {
  onlineUserIds: Set<string>;
  /** Custom availability status per user (feature #43), e.g. away / dnd. */
  userStatuses: Map<string, UserStatus>;
  setUserOnline: (userId: string) => void;
  setUserOffline: (userId: string) => void;
  setOnlineUsers: (userIds: string[]) => void;
  setUserStatus: (userId: string, status: UserStatus) => void;
  setUserStatuses: (statuses: Record<string, string>) => void;
  isOnline: (userId: string) => boolean;
}

export const usePresenceStore = create<PresenceState>((set, get) => ({
  onlineUserIds: new Set<string>(),
  userStatuses: new Map<string, UserStatus>(),

  setUserOnline: (userId: string) => {
    set((state) => {
      if (state.onlineUserIds.has(userId)) return state;
      const next = new Set(state.onlineUserIds);
      next.add(userId);
      return { onlineUserIds: next };
    });
  },

  setUserOffline: (userId: string) => {
    set((state) => {
      if (!state.onlineUserIds.has(userId)) return state;
      const next = new Set(state.onlineUserIds);
      next.delete(userId);
      return { onlineUserIds: next };
    });
  },

  setOnlineUsers: (userIds: string[]) => {
    set({ onlineUserIds: new Set(userIds) });
  },

  setUserStatus: (userId: string, status: UserStatus) => {
    set((state) => {
      const next = new Map(state.userStatuses);
      next.set(userId, status);
      return { userStatuses: next };
    });
  },

  setUserStatuses: (statuses: Record<string, string>) => {
    const next = new Map<string, UserStatus>();
    for (const [userId, status] of Object.entries(statuses)) {
      next.set(userId, status as UserStatus);
    }
    set({ userStatuses: next });
  },

  isOnline: (userId: string) => {
    return get().onlineUserIds.has(userId);
  },
}));
